import { ILanguageResource, NamedEnumLike } from './LanguageTypes'

const NAME_KEY: string = 'NAME'

/**
 * Create the enum like value full of keys.
 *
 * @param languageJSON - Language JSON file.
 *
 * @returns - The Enum value that was wanted.
 */
export const createLanguageEnum = <T extends NamedEnumLike<unknown>>(languageJSON: T): NamedEnumLike<T> =>
    ({
        ...Object.entries(languageJSON).reduce(
            (accum: Record<string, string>, [key, value]: [string, string]): Record<string, string> => {
                accum[key] = key === NAME_KEY ? value : key

                return accum
            },
            {}
        )
    }) as unknown as NamedEnumLike<typeof languageJSON>

/**
 * Update language bundle generically.
 *
 * @param languageBundle - Language bundle to add the keys to.
 * @param enumeratedLanguageItems - Enumeration of the keys for the languaging.
 * @param i18nJSON - JSON object of the [key, value] language resources.
 */
export const updateBundle = (
    languageBundle: ILanguageResource,
    enumeratedLanguageItems: Record<string, string>,
    i18nJSON: Record<string, string>
): void => {
    Object.values(enumeratedLanguageItems).forEach((key: string | number): void => {
        if (key !== NAME_KEY) {
            languageBundle[key] = i18nJSON[key]
        }
    })
}

/**
 * Generate the updateBundle function that takes in the languageBundle to be updated.
 *
 * @param enumeratedLanguageItems - Enumeration of the keys for the languaging.
 * @param i18nJSON - JSON object of the [key, value] language resources.
 *
 * @returns - The function for the updating the bundle.
 */
export const generateUpdateBundleFunction =
    (
        enumeratedLanguageItems: Record<string, string>,
        i18nJSON: Record<string, string>
    ): ((languageBundle: ILanguageResource) => void) =>
    (languageBundle: ILanguageResource): void =>
        updateBundle(languageBundle, enumeratedLanguageItems, i18nJSON)
